import React from "react";

import { Control, Controller } from "react-hook-form";

import { Input, InputProps } from "@/components/forms/BaseForm/Input/Input";
import { generateTestId } from "@/util/testIds";

export interface FormInputProps extends InputProps {
  control: Control<any, any>;
  name: string;
  pattern?: any;
  minLength?: number;
  required?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
}

// RHF has an issue where setting the value of a field with `valueAsNumber` to `null`
// causes the value to be set to 0. This changes in a later version to `NaN` but it
// still doesn't give us the ability to set a number field to `null`.
const valueAsNumber = (value: any): number | null | undefined => {
  if (value === null || value === undefined) {
    return value;
  }

  if (value === "") {
    return NaN;
  }

  return +value;
};

export const FormInput: React.FC<FormInputProps> = ({ ...props }) => {
  const isNumber = props.type === "number";

  return (
    <Controller
      name={`${props.name}`}
      control={props.control}
      rules={{
        required: props.required && {
          value: props.required,
          message: "This field is required"
        },
        pattern: props.pattern,
        minLength: props.minLength && {
          value: props.minLength,
          message: `Must be at least ${props.minLength} characters`
        }
      }}
      render={({ field, fieldState }) => (
        <Input
          {...field}
          isInvalid={!!fieldState.error}
          {...(isNumber && {
            valueAsNumber
          })}
          isDisabled={props.isDisabled}
          data-testid={generateTestId.form("input", props.name)}
        />
      )}
    />
  );
};
